import axios from 'axios';
import api from './api';
import { isGuid } from './stringHelpers';
import { logger } from './logger';

/** Thrown when the API returns 403, indicating the artwork exists but is private.
 *
 * The backend includes ``ownerId`` in the 403 response body when known
 * (delegate-recovery flow); we surface it on the error so the "Check if you
 * have access" UI can build a working ``?owner=`` link. May be undefined when
 * the backend couldn't resolve the owner (defensive fallback).
 */
export class PrivateArtworkError extends Error {
  ownerId?: string;
  constructor(message = 'This artwork is private', ownerId?: string) {
    super(message);
    this.name = 'PrivateArtworkError';
    this.ownerId = ownerId;
  }
}

/**
 * Rendition size names correspond to the pixel-width buckets defined in
 * backend/src/image_processing/size_generator.py: thumb=256, small=1024,
 * medium=2048, large=4096. The literal union is enforced everywhere renditions
 * are selected (SIZE_PREFERENCES below) so a typo like "medum" is a compile error.
 */
export type RenditionSize = 'thumb' | 'small' | 'medium' | 'large';

export interface ArtworkRendition {
  size: RenditionSize;
  format: string;
  s3Key: string;
  url?: string; // Presigned URL from API
  dimensions?: {
    width: number;
    height: number;
  };
}

export interface ArtworkImage {
  imageId: string;
  filename: string;
  size: number;
  mimeType: string;
  s3Key: string;
  status: string;
  url?: string;
  visibility?: 'public' | 'private';
  renditions: ArtworkRendition[];
}

export interface ArtworkOriginal {
  filename: string;
  size: number;
  mimeType: string;
  s3Key: string;
  url?: string; // Presigned URL from API
}

export interface Artwork {
  artworkId: string;
  userId: string;           // Owner's user ID (always populated by API)
  title?: string;           // Title from API
  creator?: string;         // Creator/artist name from API (legacy single creator)
  visibility?: 'public' | 'private';  // Visibility setting
  status: string;
  createdAt: string;
  updatedAt: string;
  // Public responses omit `original` entirely (IP protection — the unwatermarked
  // uploads-bucket URL is never shipped to unauthenticated viewers). Authenticated
  // owner and delegate responses always populate it.
  original?: ArtworkOriginal;
  renditions: ArtworkRendition[];
  coverImageUrl?: string;   // Pre-computed cover image URL (from search results)
  coverImageId?: string;    // ID of the image designated as the cover/main image
  images?: ArtworkImage[];  // Per-image data with matched renditions
  aliCatalogNumber?: string; // ALI catalog number (LL-NXX.NNNN format)
  creationDate?: { type?: string; value?: string; location?: string | string[]; display?: string } | string;
  workType?: unknown[];     // Getty vocabulary work type entries (from public API)
  techniques?: unknown[];   // Getty vocabulary technique entries (from public API)
  materials?: unknown[];    // Getty vocabulary material entries (from public API)
  signatures?: unknown[];   // Signature data (from public API)
  substrate?: unknown[];    // Substrate data (from public API)
  mediaDescription?: string; // Media description text (from public API)
  mediaAdditionalComments?: string; // Additional comments on media (from public API)
  titleAsWritten?: string;  // Title as written on artwork (from public API)
  dimensions?: unknown[];   // Measurement dimensions (from public API)
  unit?: string;            // Measurement unit (from public API)
  publicNotes?: string;     // Public notes (from public API)
  physicalLocation?: string; // Physical location (from public API)
  relatedWorks?: unknown[]; // Related works (from public API)
  creatorsList?: unknown[]; // Full creators list with roles (from public API)
  exhibitions?: unknown[];  // Exhibitions/publications (from public API)
  provenance?: unknown[];   // Provenance records (from public API)
  // Retirement fields (populated when status is CLEARED/DELETED/DUPLICATE)
  duplicateOfCatalogNumber?: string;
  duplicateOfCatalogItemId?: string;
  retirementAction?: string;
}

export interface ArtworkListResponse {
  artwork: Artwork[];
  count: number;
}

export type ImageSizePreference = 'thumbnail' | 'card' | 'detail' | 'publicDetail';

const SIZE_PREFERENCES: Record<ImageSizePreference, ReadonlyArray<RenditionSize>> = {
  thumbnail: ['thumb', 'small', 'medium', 'large'],
  card: ['medium', 'large', 'small', 'thumb'],
  detail: ['large', 'medium', 'small'],
  // IP protection: public (unauthenticated) detail views are capped at 2048 px.
  // The backend also strips `large` from public responses (authoritative), but this
  // caps frontend resolution as defense-in-depth.
  publicDetail: ['medium', 'small'],
};

/**
 * Fetch all completed artwork for the authenticated user
 */
export const getUserArtwork = async (): Promise<ArtworkListResponse> => {
  const response = await api.get('/api/catalogs');
  return response.data;
};

/**
 * Fetch a delegated user's full catalog (requires active delegation)
 */
export const getDelegatedUserArtwork = async (userId: string): Promise<ArtworkListResponse> => {
  const response = await api.get(`/api/catalogs/delegated/${userId}`);
  return response.data;
};

/**
 * Fetch a single public catalog item by ID
 * No authentication required - only returns public items
 */
export const getPublicCatalogItem = async (itemId: string): Promise<Artwork | null> => {
  try {
    const response = await api.get(`/api/catalog/public/${itemId}`);
    // Map API response to Artwork interface
    const item = response.data;
    return {
      artworkId: item.artworkId,
      userId: item.userId || '',
      title: item.title,
      creator: item.creator,
      visibility: item.visibility,
      status: item.status || 'READY',
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      // IP protection: backend sends `original: null` on public responses. Do NOT
      // substitute an empty sentinel object here — that would silently defeat the
      // optional type on Artwork.original and hide the `undefined` path from every
      // downstream consumer's optional-chaining narrowing. Let it flow through.
      original: item.original ?? undefined,
      renditions: item.renditions || [],
      creationDate: item.creationDate,
      aliCatalogNumber: item.aliCatalogNumber,
      coverImageUrl: item.coverImageUrl,
      workType: item.workType || [],
      techniques: item.techniques || [],
      materials: item.materials || [],
      signatures: item.signatures || [],
      substrate: item.substrate || [],
      mediaDescription: item.mediaDescription || '',
      mediaAdditionalComments: item.mediaAdditionalComments || '',
      titleAsWritten: item.titleAsWritten || '',
      dimensions: item.dimensions || [],
      unit: item.unit || undefined,
      publicNotes: item.publicNotes || undefined,
      physicalLocation: item.physicalLocation || undefined,
      relatedWorks: item.relatedWorks || [],
      creatorsList: item.creatorsList || [],
      exhibitions: item.exhibitions || [],
      provenance: item.provenance || [],
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 403) {
        // Backend embeds ``ownerId`` in the 403 body so delegates can recover.
        // Read defensively — older deployments may not include the field yet.
        const ownerId = (error.response.data as { ownerId?: string } | undefined)?.ownerId;
        throw new PrivateArtworkError('This artwork is private', ownerId);
      }
      if (error.response?.status === 404) {
        return null;
      }
    }
    logger.error('Failed to fetch public catalog item', error);
    return null;
  }
};

/**
 * Fetch a single authenticated catalog item by ID (own catalog only).
 * Requires authentication — returns null if not found or forbidden.
 * Use this when getPublicCatalogItem returns 403 (item is private but may belong to current user).
 */
export const getOwnCatalogItem = async (itemId: string): Promise<Artwork | null> => {
  try {
    const response = await api.get(`/api/catalogs/${itemId}`);
    const item = response.data;
    return {
      artworkId: item.artworkId,
      userId: item.userId || '',
      title: item.title,
      creator: item.creator,
      visibility: item.visibility,
      status: item.status || 'READY',
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      // IP protection: backend sends `original: null` on public responses. Do NOT
      // substitute an empty sentinel object here — that would silently defeat the
      // optional type on Artwork.original and hide the `undefined` path from every
      // downstream consumer's optional-chaining narrowing. Let it flow through.
      original: item.original ?? undefined,
      renditions: item.renditions || [],
      creationDate: item.creationDate,
      aliCatalogNumber: item.aliCatalogNumber,
      coverImageUrl: item.coverImageUrl,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 403)) {
      return null;
    }
    logger.error('Failed to fetch own catalog item', error);
    throw error;
  }
};

/**
 * Get the best rendition URL for display based on size preference.
 *
 * Uses presigned S3 URLs returned from the API for secure, time-limited access.
 *
 * @param artwork - The artwork object with renditions
 * @param preference - Size preference: 'thumbnail' (thumb-first) or 'card' (medium-first)
 */
export const getImageUrl = (artwork: Artwork, preference: ImageSizePreference): string | null => {
  // Fast path: pre-computed cover image URL from the API (CDN or presigned URL).
  if (artwork?.coverImageUrl) {
    return artwork.coverImageUrl;
  }

  const sizePreference = SIZE_PREFERENCES[preference];

  // When the item has multiple images and a designated cover, select renditions
  // from the cover image only. Without this, the flat renditions array may return
  // a rendition from a different image (e.g. the first uploaded image rather than
  // the one the user designated as the main display image). See #976.
  if (artwork?.coverImageId && artwork?.images && artwork.images.length > 0) {
    const coverImage = artwork.images.find(img => img.imageId === artwork.coverImageId)
      ?? artwork.images[0]; // fall back to first image if coverImageId not matched

    if (coverImage?.renditions && coverImage.renditions.length > 0) {
      for (const preferredSize of sizePreference) {
        const rendition = coverImage.renditions.find(r => r?.size === preferredSize);
        if (rendition?.url) {
          return rendition.url;
        }
      }
      const firstWithUrl = coverImage.renditions.find(r => r?.url);
      if (firstWithUrl?.url) {
        return firstWithUrl.url;
      }
    }

    // Cover image has no renditions; fall through to flat renditions / original
  }

  if (!artwork?.renditions || artwork.renditions.length === 0) {
    if (artwork.original?.url) {
      return artwork.original.url;
    }
    return null;
  }

  for (const preferredSize of sizePreference) {
    const rendition = artwork.renditions.find(r => r?.size === preferredSize);
    if (rendition?.url) {
      return rendition.url;
    }
  }

  const firstRenditionWithUrl = artwork.renditions.find(r => r?.url);
  if (firstRenditionWithUrl) {
    return firstRenditionWithUrl.url;
  }

  return null;
};

/**
 * Get the best rendition URL for thumbnail display.
 * Size preference: thumb > small > medium > large
 */
export const getThumbnailUrl = (artwork: Artwork): string | null => {
  return getImageUrl(artwork, 'thumbnail');
};

/**
 * Get the best rendition URL for card/grid display.
 * Size preference: medium > large > small > thumb (higher quality for cards)
 */
export const getCardImageUrl = (artwork: Artwork): string | null => {
  return getImageUrl(artwork, 'card');
};

/**
 * Get the best rendition URL for detail/gallery display.
 * Size preference: large > medium > small (highest quality for main display).
 *
 * Use this for AUTHENTICATED routes (owners and delegates). For public
 * (unauthenticated) routes, use {@link getPublicDetailImageUrl} instead
 * to cap at the 'medium' rendition for IP protection.
 */
export const getDetailImageUrl = (artwork: Artwork): string | null => {
  return getImageUrl(artwork, 'detail');
};

/**
 * Get the best rendition URL for the PUBLIC (unauthenticated) detail view.
 * Size preference: medium > small (capped at 2048 px for IP protection).
 *
 * The backend public endpoint is the authoritative layer — it strips the
 * 4096 px 'large' rendition from public responses entirely. This helper is
 * defense in depth: it narrows the frontend's rendition preference so that
 * on a normal response a 'large' URL is never selected even if one were
 * somehow present. Note: `getImageUrl`'s final fallback at
 * `firstRenditionWithUrl` could still pick 'large' in the pathological case
 * where neither 'medium' nor 'small' is present — rely on the backend layer
 * for the actual security guarantee.
 *
 * Authenticated owners and delegates should continue to use
 * {@link getDetailImageUrl}.
 */
export const getPublicDetailImageUrl = (artwork: Artwork): string | null => {
  return getImageUrl(artwork, 'publicDetail');
};

/**
 * Get display title for artwork (fallback to filename if no title)
 * Returns "Untitled" if title appears to be a GUID or is missing
 */
export const getArtworkTitle = (artwork: Artwork): string => {
  // Prefer API title if available and not a GUID
  if (artwork.title && artwork.title.trim() && !isGuid(artwork.title.trim())) {
    return artwork.title.trim();
  }

  // Fallback to original filename as title
  const filename = artwork.original?.filename || artwork.artworkId || 'Untitled';
  // Remove file extension for display
  const cleanFilename = filename.replace(/\.[^/.]+$/, '');

  // If filename looks like a GUID, return "Untitled" instead
  if (isGuid(cleanFilename)) {
    return 'Untitled';
  }

  return cleanFilename;
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Catalog Operations

export interface CreateCatalogRequest {
  title: string;
  status: 'READY' | 'DRAFT' | 'PROCESSING';
}

export interface CreateCatalogResponse {
  catalogId: string;
  title: string;
  status: string;
}

export interface UpdateCatalogStatusResponse {
  catalogId: string;
  status: string;
}

/**
 * Create a new catalog
 * Uses managed API client for automatic JWT token handling
 */
export const createCatalog = async (
  data: CreateCatalogRequest
): Promise<CreateCatalogResponse> => {
  const response = await api.post('/api/catalogs', data);
  return response.data;
};

/**
 * Update catalog status
 * Uses managed API client for automatic JWT token handling
 */
export const updateCatalogStatus = async (
  catalogId: string,
  status: 'READY' | 'DRAFT' | 'PROCESSING'
): Promise<UpdateCatalogStatusResponse> => {
  const response = await api.put(`/api/catalogs/${catalogId}`, { status });
  return response.data;
};
